'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type BillingCycle = 'HALF_YEARLY' | 'YEARLY';

interface PlanConfig {
  id: string;
  name: string;
  description: string;
  prices: {
    HALF_YEARLY: { current: number; original?: number };
    YEARLY: { current: number; original?: number };
  } | null;
  cardLabel: string;
  popular?: boolean;
  isCustom?: boolean;
  alertsByCycle?: Partial<Record<BillingCycle, number>>;
  features: Array<{ text: string; included: boolean }>;
}

interface SelectedPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice?: number;
  cards: number;
  popular?: boolean;
  savings?: string;
  features: string[];
  billingCycle: BillingCycle;
  isCustom?: boolean;
}

interface PlanSelectorProps {
  onPlanSelect: (plan: SelectedPlan, quantity: number) => void;
}

const plans: PlanConfig[] = [
  {
    id: 'plus',
    name: 'ParkPing Plus',
    description: 'Best for individual vehicle owners',
    prices: {
      HALF_YEARLY: { current: 99, original: 149 },
      YEARLY: { current: 199, original: 299 },
    },
    cardLabel: 'Per card',
    alertsByCycle: { HALF_YEARLY: 15, YEARLY: 30 },
    features: [
      { text: 'Parking SMS notification', included: true },
      { text: 'Private contact protection', included: true },
      { text: 'Call details to vehicle owner', included: false },
    ],
  },
  {
    id: 'prime',
    name: 'ParkPing Prime',
    description: 'Most preferred for regular city driving',
    prices: {
      HALF_YEARLY: { current: 199, original: 349 },
      YEARLY: { current: 349, original: 599 },
    },
    cardLabel: 'Per card',
    popular: true,
    alertsByCycle: { HALF_YEARLY: 25, YEARLY: 50 },
    features: [
      { text: 'Parking SMS notification', included: true },
      { text: 'Private contact protection', included: true },
      { text: 'Call details to vehicle owner', included: true },
    ],
  },
  {
    id: 'pro',
    name: 'ParkPing Pro',
    description: 'For societies, campuses, and enterprise deployments',
    prices: null,
    cardLabel: 'Custom pricing',
    isCustom: true,
    features: [
      { text: 'Parking SMS notification', included: true },
      { text: 'Private contact protection', included: true },
      { text: 'Custom alert limits', included: true },
      { text: 'Call details to vehicle owner', included: true },
      { text: 'Dedicated support', included: true },
    ],
  },
];

function formatINR(amount: number) {
  return amount.toLocaleString('en-IN');
}

function getIncludedFeatureTexts(plan: PlanConfig, cycle: BillingCycle) {
  const baseIncluded = plan.features.filter((f) => f.included).map((f) => f.text);
  const alerts = plan.alertsByCycle?.[cycle];
  if (!alerts) return baseIncluded;

  const alertsLine = `Up to ${alerts} alerts per plan`;
  const insertAfter = baseIncluded.findIndex((t) => t === 'Private contact protection');
  if (insertAfter >= 0) {
    const result = baseIncluded.slice();
    result.splice(insertAfter + 1, 0, alertsLine);
    return result;
  }

  return [...baseIncluded, alertsLine];
}

export default function PlanSelector({ onPlanSelect }: PlanSelectorProps) {
  const [selectedPlanId, setSelectedPlanId] = useState('plus');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('YEARLY');

  const emitSelection = (planId: string, cycle: BillingCycle) => {
    const plan = plans.find((item) => item.id === planId);
    if (!plan) return;

    const priceInfo = plan.prices?.[cycle];
    const current = priceInfo?.current || 0;
    const original = priceInfo?.original;
    const savings =
      original && original > current
        ? `Save INR ${(original - current).toLocaleString('en-IN')}`
        : undefined;

    onPlanSelect(
      {
        id: plan.id,
        name: plan.name,
        description: plan.description,
        price: current,
        originalPrice: original,
        cards: 1,
        popular: plan.popular,
        savings,
        features: getIncludedFeatureTexts(plan, cycle),
        billingCycle: cycle,
        isCustom: plan.isCustom,
      },
      1
    );
  };

  useEffect(() => {
    emitSelection(selectedPlanId, billingCycle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlanChange = (planId: string) => {
    setSelectedPlanId(planId);
    emitSelection(planId, billingCycle);
  };

  const handleCycleChange = (cycle: BillingCycle) => {
    setBillingCycle(cycle);
    emitSelection(selectedPlanId, cycle);
  };

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  const selectedPriceInfo = selectedPlan?.prices?.[billingCycle];

  return (
    <div className="space-y-7">
      <div className="space-y-2">
        <h2 className="text-2xl font-heading font-extrabold text-foreground">
          Choose your ParkPing plan
        </h2>
        <p className="text-sm text-muted-foreground">
          Choose duration and plan. Plan price is for service activation. One-time card cost applies only for the first purchase.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="inline-flex p-1 rounded-2xl bg-muted/40 border border-border">
          <button
            type="button"
            onClick={() => handleCycleChange('HALF_YEARLY')}
            className={[
              'px-4 py-2 rounded-xl text-sm font-semibold transition',
              billingCycle === 'HALF_YEARLY'
                ? 'bg-background text-foreground shadow-subtle border border-border'
                : 'text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            6 months
          </button>
          <button
            type="button"
            onClick={() => handleCycleChange('YEARLY')}
            className={[
              'px-4 py-2 rounded-xl text-sm font-semibold transition flex items-center gap-2',
              billingCycle === 'YEARLY'
                ? 'bg-background text-foreground shadow-subtle border border-border'
                : 'text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            12 months
            <span className="text-[10px] font-bold rounded-full bg-accent/15 text-foreground border border-accent/20 px-2 py-0.5">
              Best value
            </span>
          </button>
        </div>
      </div>

      {/* Plan selector (checkout-style cards) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((plan) => {
          const isSelected = selectedPlanId === plan.id;
          const p = plan.prices?.[billingCycle];
          const isCustom = Boolean(plan.isCustom || !p);
          const includedTexts = getIncludedFeatureTexts(plan, billingCycle);
          const effectiveFeatures = plan.features.map((f) => ({
            ...f,
            text:
              f.included && includedTexts.includes(f.text)
                ? f.text
                : f.text,
          }));

          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => handlePlanChange(plan.id)}
              className={[
                'relative w-full text-left rounded-2xl border p-6 transition-all duration-300',
                'bg-background',
                'focus:outline-none focus:ring-2 focus:ring-accent/25',
                isSelected
                  ? 'border-accent/60 shadow-cta'
                  : 'border-border hover:border-accent/30 hover:shadow-card hover:-translate-y-0.5',
              ].join(' ')}
            >
              {plan.popular ? (
                <div className="absolute -top-3 left-6 inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-bold text-accent-foreground shadow-button">
                  <Icon name="SparklesIcon" size={14} />
                  Most popular
                </div>
              ) : null}

              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-heading font-extrabold text-foreground leading-tight">
                    {plan.name}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{plan.description}</div>
                </div>

                <div
                  className={`mt-0.5 w-7 h-7 rounded-full border flex items-center justify-center ${
                    isSelected ? 'border-accent bg-accent' : 'border-border bg-white'
                  }`}
                  aria-hidden="true"
                >
                  {isSelected ? (
                    <Icon name="CheckIcon" size={14} className="text-accent-foreground" />
                  ) : null}
                </div>
              </div>

              <div className="mt-5">
                {isCustom ? (
                  <>
                    <div className="text-3xl font-heading font-extrabold text-foreground">Custom</div>
                    <div className="text-xs text-muted-foreground mt-1">{plan.cardLabel}</div>
                  </>
                ) : (
                  <>
                    {(() => {
                      const price = p!;
                      const original = price.original;
                      const current = price.current;
                      const hasPlanDiscount = Boolean(original && original > current);

                      return (
                        <>
                    <div className="flex items-end gap-2">
                      <span className="text-sm font-bold text-muted-foreground leading-none">INR</span>
                      <span className="text-4xl font-heading font-extrabold text-foreground tracking-tight leading-none">
                        {formatINR(current)}
                      </span>
                      {hasPlanDiscount ? (
                        <span className="text-sm text-muted-foreground line-through pb-0.5">
                          INR {formatINR(original!)}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      per card for {billingCycle === 'YEARLY' ? '12 months' : '6 months'}
                    </div>
                    {hasPlanDiscount ? (
                      <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-accent/10 border border-accent/20 px-3 py-1 text-xs font-semibold text-foreground">
                        <Icon name="TagIcon" size={14} className="text-accent" />
                        Save {Math.round(((original! - current) / original!) * 100)}%
                      </div>
                    ) : null}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-border">
                <div className="space-y-2">
                  {(() => {
                    // Render base feature list but replace the alerts line for plans that have alertsByCycle.
                    const withDynamicAlerts = plan.alertsByCycle
                      ? [
                          ...plan.features.filter((f) => f.text !== 'Custom alert limits'),
                        ]
                      : plan.features;

                    const rendered = withDynamicAlerts.slice(0, 6);
                    const alertsValue = plan.alertsByCycle?.[billingCycle];
                    const dynamicAlertsText = alertsValue
                      ? `Up to ${alertsValue} alerts per plan`
                      : null;

                    // Insert dynamic alerts after private contact protection for Plus/Prime.
                    if (dynamicAlertsText) {
                      const idx = rendered.findIndex((f) => f.text === 'Private contact protection');
                      if (idx >= 0) {
                        rendered.splice(idx + 1, 0, { text: dynamicAlertsText, included: true });
                      } else {
                        rendered.push({ text: dynamicAlertsText, included: true });
                      }
                    }

                    return rendered;
                  })().map((f) => (
                    <div
                      key={f.text}
                      className={[
                        'flex items-start gap-2 text-xs leading-snug',
                        f.included ? 'text-muted-foreground' : 'text-muted-foreground/60',
                      ].join(' ')}
                    >
                      <Icon
                        name={f.included ? 'CheckCircleIcon' : 'XCircleIcon'}
                        size={16}
                        className={f.included ? 'text-accent mt-0.5' : 'text-muted-foreground/40 mt-0.5'}
                      />
                      <span>{f.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
